import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  LoadingOverlay,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconBolt,
  IconEdit,
  IconInfoCircle,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconTrash,
} from '@tabler/icons-react';
import type {
  Payment,
  PaymentRequirement,
  PaymentRequirementTarget,
  Student,
} from '@pta/types';
import {
  deletePayment,
  fetchGroupPayments,
} from '../api/payments';
import {
  fetchGroupRequirements,
  fetchRequirement,
} from '../api/payment-requirements';
import { fetchStudentsByGroup } from '../api/core-entities';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useMoneyFormatter } from '../util/money';
import { PaymentFormModal } from '../components/PaymentFormModal';
import { QuickPaymentConfirmModal } from '../components/QuickPaymentConfirmModal';
import { StudentDebtsModal } from '../components/StudentDebtsModal';

/**
 * Befizetések lapja — csoport-szintű lista.
 *
 * Új befizetés rögzítéséhez két UI-mód:
 *   - "Egyenkénti" — requirement + target picker → normál PaymentFormModal.
 *   - "Osztálynézet" — egy választott requirement-en az összes target táblázatban,
 *     mindegyiknél "Teljes hátralék" (gyors modal) + "Részfizetés" (teljes form) gomb.
 *
 * Pénz-mezők formázása: a csoport `GroupLocale`-jából (useMoneyFormatter).
 */
type ViewMode = 'picker' | 'class';

export function PaymentsPage() {
  const { activeGroupId, myGroups, activeRole } = useAuth();
  const { money } = useMoneyFormatter();
  const activeGroup = useMemo(
    () => myGroups.find((g) => g.id === activeGroupId) ?? null,
    [myGroups, activeGroupId],
  );

  const canEdit =
    activeRole === 'pta_cashier' ||
    activeRole === 'institution_admin' ||
    activeRole === 'country_admin' ||
    activeRole === 'systemadmin' ||
    activeRole === 'applicationadmin';

  const [payments, setPayments] = useState<Payment[]>([]);
  const [requirements, setRequirements] = useState<PaymentRequirement[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Új payment kontextus
  const [viewMode, setViewMode] = useState<ViewMode>('picker');
  const [pickerReqId, setPickerReqId] = useState<string | null>(null);
  const [pickerTargets, setPickerTargets] = useState<PaymentRequirementTarget[]>([]);
  const [pickerTargetId, setPickerTargetId] = useState<string | null>(null);
  const [paidTotalsByTarget, setPaidTotalsByTarget] = useState<Map<number, string>>(
    new Map(),
  );
  const [classOnlyDebt, setClassOnlyDebt] = useState(true);

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure();
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [editTarget, setEditTarget] = useState<PaymentRequirementTarget | null>(null);

  const [quickOpened, { open: openQuick, close: closeQuick }] = useDisclosure();
  const [quickTarget, setQuickTarget] = useState<PaymentRequirementTarget | null>(null);
  const [quickRemaining, setQuickRemaining] = useState<string>('0');

  const [debtsOpened, { open: openDebts, close: closeDebts }] = useDisclosure();
  const [debtsStudent, setDebtsStudent] = useState<{ id: number; name: string } | null>(
    null,
  );

  const studentById = useMemo(() => {
    const map = new Map<number, Student>();
    for (const s of students) map.set(s.id, s);
    return map;
  }, [students]);

  const targetById = useMemo(() => {
    const map = new Map<number, PaymentRequirementTarget>();
    for (const t of pickerTargets) map.set(t.id, t);
    return map;
  }, [pickerTargets]);

  const requirementById = useMemo(() => {
    const map = new Map<number, PaymentRequirement>();
    for (const r of requirements) map.set(r.id, r);
    return map;
  }, [requirements]);

  const load = async () => {
    if (activeGroupId === null) return;
    setLoading(true);
    setError(null);
    try {
      const [pmts, reqs, studs] = await Promise.all([
        fetchGroupPayments(activeGroupId),
        fetchGroupRequirements(activeGroupId),
        fetchStudentsByGroup(activeGroupId),
      ]);
      setPayments(pmts);
      setRequirements(
        reqs.filter((r) => r.status === 'published' || r.status === 'closed'),
      );
      setStudents(studs);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [activeGroupId]);

  useEffect(() => {
    if (!pickerReqId) {
      setPickerTargets([]);
      setPaidTotalsByTarget(new Map());
      return;
    }
    void (async () => {
      try {
        const full = await fetchRequirement(Number(pickerReqId), { includeTargets: true });
        setPickerTargets(full.targets ?? []);
        const totals = new Map<number, string>();
        for (const t of full.targets ?? []) {
          const sum = payments
            .filter((p) => p.targetId === t.id)
            .reduce((acc, p) => acc + Number(p.amount), 0);
          totals.set(t.id, sum.toFixed(2));
        }
        setPaidTotalsByTarget(totals);
        setPickerTargetId(null);
      } catch (e) {
        notifications.show({
          color: 'red',
          message: e instanceof ApiError ? e.message : String(e),
        });
      }
    })();
  }, [pickerReqId, payments]);

  if (activeGroupId === null) {
    return (
      <Stack gap="md">
        <Title order={2}>Befizetések</Title>
        <Alert color="yellow" icon={<IconInfoCircle size={18} />}>
          Nincs aktív csoport kiválasztva.
        </Alert>
      </Stack>
    );
  }

  const handleNewPayment = () => {
    if (!pickerReqId || !pickerTargetId) {
      notifications.show({
        color: 'yellow',
        message: 'Válassz előírást és diákot az új befizetéshez.',
      });
      return;
    }
    const t = targetById.get(Number(pickerTargetId));
    if (!t) return;
    setEditingPayment(null);
    setEditTarget(t);
    openModal();
  };

  const handleClassFullPayment = (target: PaymentRequirementTarget, remaining: number) => {
    setQuickTarget(target);
    setQuickRemaining(remaining.toFixed(2));
    openQuick();
  };

  const handleClassPartialPayment = (target: PaymentRequirementTarget) => {
    setEditingPayment(null);
    setEditTarget(target);
    openModal();
  };

  const handleStudentClick = (studentId: number, name: string) => {
    setDebtsStudent({ id: studentId, name });
    openDebts();
  };

  const handleEdit = async (p: Payment) => {
    let target: PaymentRequirementTarget | null = targetById.get(p.targetId) ?? null;
    if (!target) {
      try {
        for (const r of requirements) {
          const full = await fetchRequirement(r.id, { includeTargets: true });
          const t = (full.targets ?? []).find((x) => x.id === p.targetId);
          if (t) {
            target = t;
            break;
          }
        }
      } catch {
        /* ignore */
      }
    }

    if (!target) {
      notifications.show({
        color: 'red',
        message: 'A target nem található. Frissítsd az oldalt.',
      });
      return;
    }
    setEditingPayment(p);
    setEditTarget(target);
    openModal();
  };

  const handleDelete = (p: Payment) => {
    modals.openConfirmModal({
      title: 'Befizetés törlése',
      children: (
        <Stack gap="xs">
          <Text>Biztosan törlöd ezt a befizetést? Ez a művelet auditálva van.</Text>
          <Text fw={500}>
            {money(p.amount)} — {p.paidAt}
          </Text>
        </Stack>
      ),
      labels: { confirm: 'Törlés', cancel: 'Mégse' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deletePayment(p.id);
          notifications.show({ color: 'green', message: 'Törölve.' });
          await load();
        } catch (e) {
          notifications.show({
            color: 'red',
            message: e instanceof ApiError ? e.message : String(e),
          });
        }
      },
    });
  };

  const requirementOptions = requirements.map((r) => ({
    value: String(r.id),
    label: `#${r.id} — ${r.title}`,
  }));

  const targetOptions = pickerTargets
    .filter((t) => !t.waived)
    .map((t) => {
      const s = studentById.get(t.studentId);
      const name = s ? `${s.lastName} ${s.firstName}` : `#${t.studentId}`;
      const paid = paidTotalsByTarget.get(t.id) ?? '0';
      const remaining = Number(t.expectedAmount) - Number(paid);
      const remainingLabel =
        remaining > 0 ? ` (hátra: ${money(remaining)})` : ' (teljesítve)';
      return { value: String(t.id), label: `${name}${remainingLabel}` };
    });

  // Osztálynézet target-listája — névsorban (lastName, firstName).
  const classRows = useMemo(() => {
    const rows = pickerTargets.map((t) => {
      const s = studentById.get(t.studentId);
      const name = s ? `${s.lastName} ${s.firstName}` : `#${t.studentId}`;
      const paid = Number(paidTotalsByTarget.get(t.id) ?? '0');
      const expected = Number(t.expectedAmount);
      const remaining = Math.max(0, expected - paid);
      return { target: t, name, paid, expected, remaining };
    });
    rows.sort((a, b) => a.name.localeCompare(b.name, 'hu'));
    if (classOnlyDebt) {
      return rows.filter((r) => !r.target.waived && r.remaining > 0);
    }
    return rows;
  }, [pickerTargets, studentById, paidTotalsByTarget, classOnlyDebt]);

  const StudentLink = ({ id, name }: { id: number; name: string }) => (
    <Anchor
      component="button"
      type="button"
      onClick={() => handleStudentClick(id, name)}
      style={{ textAlign: 'left' }}
    >
      {name}
    </Anchor>
  );

  return (
    <Stack gap="md" pos="relative">
      <LoadingOverlay visible={loading} />

      <Group justify="space-between">
        <Title order={2}>{activeGroup?.name ?? `#${activeGroupId}`} — Befizetések</Title>
        <Tooltip label="Frissítés">
          <ActionIcon onClick={() => void load()} variant="light">
            <IconRefresh size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {error && <Alert color="red">{error}</Alert>}

      {canEdit && (
        <Card withBorder>
          <Stack gap="xs">
            <Group justify="space-between">
              <Text fw={500}>Új befizetés rögzítése</Text>
              <SegmentedControl
                size="xs"
                data={[
                  { value: 'picker', label: 'Egyenkénti' },
                  { value: 'class', label: 'Osztálynézet' },
                ]}
                value={viewMode}
                onChange={(v) => setViewMode(v as ViewMode)}
              />
            </Group>

            {viewMode === 'picker' ? (
              <>
                <Group grow>
                  <Select
                    label="Előírás"
                    placeholder="válassz publikált/lezárt előírást"
                    data={requirementOptions}
                    value={pickerReqId}
                    onChange={setPickerReqId}
                    searchable
                    clearable
                    disabled={requirements.length === 0}
                  />
                  <Select
                    label="Diák"
                    placeholder="válassz diákot (target)"
                    data={targetOptions}
                    value={pickerTargetId}
                    onChange={setPickerTargetId}
                    searchable
                    clearable
                    disabled={!pickerReqId || targetOptions.length === 0}
                  />
                </Group>
                <Group justify="end">
                  <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={handleNewPayment}
                    disabled={!pickerTargetId}
                  >
                    Befizetés rögzítése
                  </Button>
                </Group>
              </>
            ) : (
              <>
                <Group grow>
                  <Select
                    label="Előírás"
                    placeholder="válassz publikált/lezárt előírást"
                    data={requirementOptions}
                    value={pickerReqId}
                    onChange={setPickerReqId}
                    searchable
                    clearable
                    disabled={requirements.length === 0}
                  />
                  <Stack gap={4} justify="end" pb={4}>
                    <Checkbox
                      label="Csak hiányosak"
                      checked={classOnlyDebt}
                      onChange={(e) => setClassOnlyDebt(e.currentTarget.checked)}
                    />
                  </Stack>
                </Group>
                {pickerReqId && classRows.length > 0 && (
                  <Card withBorder p={0} radius="sm">
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Diák</Table.Th>
                          <Table.Th ta="right">Várt</Table.Th>
                          <Table.Th ta="right">Fizetve</Table.Th>
                          <Table.Th ta="right">Hátralék</Table.Th>
                          <Table.Th style={{ width: 280 }}>Művelet</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {classRows.map((row) => {
                          const { target, name, paid, expected, remaining } = row;
                          if (target.waived) {
                            return (
                              <Table.Tr key={target.id}>
                                <Table.Td>
                                  <StudentLink id={target.studentId} name={name} />
                                </Table.Td>
                                <Table.Td ta="right">{money(target.expectedAmount)}</Table.Td>
                                <Table.Td colSpan={2}>
                                  <Badge color="orange" variant="light" size="sm">
                                    Mentesítve
                                  </Badge>
                                  {target.waiveReason && (
                                    <Text size="xs" c="dimmed">{target.waiveReason}</Text>
                                  )}
                                </Table.Td>
                                <Table.Td>—</Table.Td>
                              </Table.Tr>
                            );
                          }
                          const fullyPaid = remaining === 0;
                          return (
                            <Table.Tr key={target.id}>
                              <Table.Td>
                                <StudentLink id={target.studentId} name={name} />
                              </Table.Td>
                              <Table.Td ta="right">{money(expected)}</Table.Td>
                              <Table.Td ta="right">{money(paid)}</Table.Td>
                              <Table.Td ta="right">
                                {fullyPaid ? (
                                  <Badge color="teal" variant="light" size="sm">
                                    Teljesítve
                                  </Badge>
                                ) : (
                                  <Text fw={500}>{money(remaining)}</Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                {!fullyPaid && (
                                  <Group gap={4}>
                                    <Button
                                      size="xs"
                                      color="teal"
                                      leftSection={<IconBolt size={14} />}
                                      onClick={() =>
                                        handleClassFullPayment(target, remaining)
                                      }
                                    >
                                      Teljes hátralék
                                    </Button>
                                    <Button
                                      size="xs"
                                      variant="light"
                                      leftSection={<IconPencil size={14} />}
                                      onClick={() => handleClassPartialPayment(target)}
                                    >
                                      Részfizetés
                                    </Button>
                                  </Group>
                                )}
                              </Table.Td>
                            </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                  </Card>
                )}
                {pickerReqId && classRows.length === 0 && (
                  <Alert color="green" variant="light" icon={<IconInfoCircle size={18} />}>
                    {classOnlyDebt
                      ? 'Nincs hiányos target ezen az előíráson.'
                      : 'Az előíráson nincs target.'}
                  </Alert>
                )}
              </>
            )}
          </Stack>
        </Card>
      )}

      <Card withBorder p={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Dátum</Table.Th>
              <Table.Th>Előírás</Table.Th>
              <Table.Th>Diák</Table.Th>
              <Table.Th ta="right">Összeg</Table.Th>
              <Table.Th>Mód</Table.Th>
              <Table.Th>Megjegyzés</Table.Th>
              {canEdit && <Table.Th style={{ width: 90 }}>Műveletek</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {payments.length === 0 && !loading && (
              <Table.Tr>
                <Table.Td colSpan={canEdit ? 7 : 6} align="center">
                  Nincs rögzített befizetés.
                </Table.Td>
              </Table.Tr>
            )}
            {payments.map((p) => {
              const target = targetById.get(p.targetId);
              const studentName = target
                ? (() => {
                    const s = studentById.get(target.studentId);
                    return s ? `${s.lastName} ${s.firstName}` : `#${target.studentId}`;
                  })()
                : `target #${p.targetId}`;
              const reqLabel = target
                ? (() => {
                    const r = requirementById.get(target.requirementId);
                    return r ? `#${r.id} ${r.title}` : `#${target.requirementId}`;
                  })()
                : '—';
              return (
                <Table.Tr key={p.id}>
                  <Table.Td>{p.paidAt}</Table.Td>
                  <Table.Td>{reqLabel}</Table.Td>
                  <Table.Td>
                    {target ? (
                      <StudentLink id={target.studentId} name={studentName} />
                    ) : (
                      studentName
                    )}
                  </Table.Td>
                  <Table.Td ta="right">
                    <strong>{money(p.amount)}</strong>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" size="sm">{p.paymentMethod}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed" lineClamp={2}>
                      {p.notes ?? '—'}
                    </Text>
                  </Table.Td>
                  {canEdit && (
                    <Table.Td>
                      <Group gap={4}>
                        <ActionIcon variant="subtle" onClick={() => void handleEdit(p)}>
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => handleDelete(p)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>

      {editTarget && (
        <PaymentFormModal
          opened={modalOpened}
          onClose={() => {
            closeModal();
            setEditingPayment(null);
            setEditTarget(null);
          }}
          groupId={activeGroupId}
          target={editTarget}
          studentName={(() => {
            const s = studentById.get(editTarget.studentId);
            return s ? `${s.lastName} ${s.firstName}` : `#${editTarget.studentId}`;
          })()}
          currency={
            requirementById.get(editTarget.requirementId)?.currency ?? 'HUF'
          }
          paidSoFar={paidTotalsByTarget.get(editTarget.id) ?? '0'}
          payment={editingPayment}
          onSaved={async () => {
            closeModal();
            setEditingPayment(null);
            setEditTarget(null);
            await load();
          }}
        />
      )}

      {quickTarget && (
        <QuickPaymentConfirmModal
          opened={quickOpened}
          onClose={() => {
            closeQuick();
            setQuickTarget(null);
          }}
          groupId={activeGroupId}
          target={quickTarget}
          studentName={(() => {
            const s = studentById.get(quickTarget.studentId);
            return s ? `${s.lastName} ${s.firstName}` : `#${quickTarget.studentId}`;
          })()}
          currency={
            requirementById.get(quickTarget.requirementId)?.currency ?? 'HUF'
          }
          remaining={quickRemaining}
          onSaved={async () => {
            closeQuick();
            setQuickTarget(null);
            await load();
          }}
        />
      )}

      {debtsStudent && (
        <StudentDebtsModal
          opened={debtsOpened}
          onClose={() => {
            closeDebts();
            setDebtsStudent(null);
          }}
          studentId={debtsStudent.id}
          studentName={debtsStudent.name}
          requirements={requirements}
          payments={payments}
        />
      )}
    </Stack>
  );
}
